/**
 * The 80 work-order photos this browser most recently stored or painted.
 * IndexedDB database WorkOrderPhotos. Old photo databases are deleted.
 */

const PHOTO_CACHE_LIMIT = 80;
const DB_NAME = "WorkOrderPhotos";
const STORE = "photos";
const DB_VERSION = 1;
const OLD_PHOTO_DB_NAMES = ["WorkOrderReviewPhotos", "WorkOrderPhotoBlobs"];

let dbPromise = null;

function photoId(id) {
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

function rememberPhotos(records, photo, limit = PHOTO_CACHE_LIMIT) {
  const id = photoId(photo && photo.id);
  const list = Array.isArray(records) ? records.slice() : [];
  if (id == null) return list.slice(0, limit);
  const cachedAt = Number.isFinite(Number(photo.cachedAt))
    ? Number(photo.cachedAt)
    : Date.now();
  const next = list.filter((row) => photoId(row && row.id) !== id);
  next.push({ ...photo, id, cachedAt });
  next.sort(
    (a, b) =>
      (b.cachedAt || 0) - (a.cachedAt || 0) || photoId(b.id) - photoId(a.id),
  );
  return next.slice(0, limit);
}

function forgetPhoto(records, id) {
  const drop = photoId(id);
  return (Array.isArray(records) ? records : []).filter(
    (row) => photoId(row && row.id) !== drop,
  );
}

function photoModifiedTime(photo) {
  const t = Date.parse(photo && photo.modifiedOn);
  return Number.isFinite(t) ? t : 0;
}

function hasDisplayableBlob(row) {
  const blob = row && row.blob;
  if (!blob || typeof blob.size !== "number" || blob.size <= 0) return false;
  const type = String(blob.type || "");
  if (/json|text\/html|text\/plain/i.test(type)) return false;
  return true;
}

function cachedPhotosForWorkOrder(records, workOrderId) {
  const wo = Number(workOrderId);
  return (Array.isArray(records) ? records : [])
    .filter(
      (row) => Number(row && row.workOrderId) === wo && hasDisplayableBlob(row),
    )
    .sort(
      (a, b) =>
        photoModifiedTime(b) - photoModifiedTime(a) ||
        photoId(b.id) - photoId(a.id),
    );
}

function forgetOldPhotoDatabases(factory, names = OLD_PHOTO_DB_NAMES) {
  const ids =
    factory || (typeof indexedDB !== "undefined" ? indexedDB : null);
  if (!ids || typeof ids.deleteDatabase !== "function") {
    return Promise.resolve();
  }
  const dbNames = Array.isArray(names) ? names : [];
  return Promise.all(
    dbNames.map(
      (name) =>
        new Promise((resolve) => {
          let request;
          try {
            request = ids.deleteDatabase(name);
          } catch (err) {
            resolve();
            return;
          }
          if (!request) {
            resolve();
            return;
          }
          request.onsuccess = () => resolve();
          request.onerror = () => resolve();
          request.onblocked = () => resolve();
        }),
    ),
  );
}

function idbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function waitForTx(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function ownBlob(blob) {
  if (!hasDisplayableBlob({ blob })) return Promise.resolve(undefined);
  if (typeof blob.arrayBuffer !== "function") return Promise.resolve(blob);
  return blob.arrayBuffer().then((buffer) => {
    if (!buffer || buffer.byteLength === 0) return undefined;
    return new Blob([buffer], { type: blob.type || "" });
  });
}

function createPhotoCache({
  dbName,
  oldDbNames = [],
  limit = PHOTO_CACHE_LIMIT,
} = {}) {
  const STORE = "photos";
  const DB_VERSION = 1;
  let dbPromise = null;

  function connect() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, DB_VERSION);
      request.onerror = () => reject(request.error);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "id" });
          store.createIndex("workOrderId", "workOrderId", { unique: false });
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        db.onclose = () => {
          dbPromise = null;
        };
        resolve(db);
      };
    });
  }

  function openDb() {
    if (typeof indexedDB === "undefined") {
      return Promise.reject(new Error("indexedDB unavailable"));
    }
    if (!dbPromise) {
      dbPromise = forgetOldPhotoDatabases(undefined, oldDbNames)
        .then(connect)
        .catch((err) => {
          dbPromise = null;
          throw err;
        });
    }
    return dbPromise;
  }

  async function getCachedPhotosForWorkOrder(workOrderId) {
    const wo = Number(workOrderId);
    if (!Number.isFinite(wo)) return [];
    try {
      const db = await openDb();
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const index = store.index("workOrderId");
      const rows = (await idbRequest(index.getAll(wo))) || [];
      await waitForTx(tx);
      const owned = [];
      for (const row of rows) {
        const blob = await ownBlob(row && row.blob);
        if (!blob) continue;
        owned.push({ ...row, blob });
      }
      return cachedPhotosForWorkOrder(owned, wo);
    } catch (err) {
      console.error("Error reading work order photo cache:", err);
      return [];
    }
  }

  async function cachedPhotoCount() {
    try {
      const db = await openDb();
      const tx = db.transaction(STORE, "readonly");
      const keys = (await idbRequest(tx.objectStore(STORE).getAllKeys())) || [];
      await waitForTx(tx);
      return keys.length;
    } catch (err) {
      console.error("Error counting work order photo cache:", err);
      return limit;
    }
  }

  async function writeRecord(record) {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(record);
    await waitForTx(tx);
  }

  async function pruneToLimit() {
    const db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const rows = (await idbRequest(tx.objectStore(STORE).getAll())) || [];
    await waitForTx(tx);
    const keep = new Set(
      rows
        .slice()
        .sort(
          (a, b) =>
            (b.cachedAt || 0) - (a.cachedAt || 0) ||
            photoId(b.id) - photoId(a.id),
        )
        .slice(0, limit)
        .map((row) => photoId(row.id)),
    );
    const remove = rows
      .map((row) => photoId(row.id))
      .filter((id) => id != null && !keep.has(id));
    if (!remove.length) return;
    const dropTx = db.transaction(STORE, "readwrite");
    const store = dropTx.objectStore(STORE);
    remove.forEach((id) => store.delete(id));
    await waitForTx(dropTx);
  }

  async function putCachedPhoto(photo) {
    const id = photoId(photo && photo.id);
    if (id == null || !hasDisplayableBlob(photo)) return;
    await writeRecord({
      id,
      workOrderId: Number(photo.workOrderId),
      caption: photo.caption || "",
      modifiedOn: photo.modifiedOn || null,
      cachedAt: Date.now(),
      blob: photo.blob,
    });
    await pruneToLimit();
  }

  async function putCachedPhotoPrefill(photo) {
    if ((await cachedPhotoCount()) >= limit) return;
    const id = photoId(photo && photo.id);
    if (id == null || !hasDisplayableBlob(photo)) return;
    if ((await cachedPhotoCount()) >= limit) return;
    await writeRecord({
      id,
      workOrderId: Number(photo.workOrderId),
      caption: photo.caption || "",
      modifiedOn: photo.modifiedOn || null,
      cachedAt: Date.now(),
      blob: photo.blob,
    });
  }

  async function removeCachedPhoto(id) {
    const key = photoId(id);
    if (key == null) return;
    try {
      const db = await openDb();
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      await waitForTx(tx);
    } catch (err) {
      console.error("Error removing work order photo cache:", err);
    }
  }

  async function touchCachedPhotos(ids) {
    const keys = (ids || []).map(photoId).filter((id) => id != null);
    if (!keys.length) return;
    try {
      const now = Date.now();
      const db = await openDb();
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      for (const id of keys) {
        const row = await idbRequest(store.get(id));
        if (!row) continue;
        store.put({ ...row, cachedAt: now });
      }
      await waitForTx(tx);
    } catch (err) {
      console.error("Error touching work order photo cache:", err);
    }
  }

  return {
    getCachedPhotosForWorkOrder,
    cachedPhotoCount,
    putCachedPhoto,
    putCachedPhotoPrefill,
    removeCachedPhoto,
    touchCachedPhotos,
  };
}

const workOrderPhotos = createPhotoCache({
  dbName: DB_NAME,
  oldDbNames: OLD_PHOTO_DB_NAMES,
});

module.exports = {
  PHOTO_CACHE_LIMIT,
  OLD_PHOTO_DB_NAMES,
  DB_NAME,
  rememberPhotos,
  forgetPhoto,
  cachedPhotosForWorkOrder,
  forgetOldPhotoDatabases,
  createPhotoCache,
  getCachedPhotosForWorkOrder: workOrderPhotos.getCachedPhotosForWorkOrder,
  cachedPhotoCount: workOrderPhotos.cachedPhotoCount,
  putCachedPhoto: workOrderPhotos.putCachedPhoto,
  putCachedPhotoPrefill: workOrderPhotos.putCachedPhotoPrefill,
  removeCachedPhoto: workOrderPhotos.removeCachedPhoto,
  touchCachedPhotos: workOrderPhotos.touchCachedPhotos,
};
